import React, { FC, useCallback, useMemo, useState } from 'react';
import { connect } from 'react-redux';
import { withRouter } from 'react-router';
import { Button, Dropdown, Menu, Popover, message } from 'antd';
import {
	fetchAllPartsForSupplier,
	getPartialPartsUploadBulkStatus,
	partsRestCrudThunksForSupplier,
	uploadPartialPartsBulk,
} from '../../thunks/parts_thunks';
import { EmptyState } from '../empty_state/EmptyState';
import PartRowDisplay from '../part_row_display/PartRowDisplay';
import { PARTS_CRUD_ACTION_CREATORS } from '../../actions/parts_actions';
import PaginatedReduxTableWithHeader from '../common/PaginatedReduxTableWithHeader';
import AccessPermissionChecker from '../common/AccessPermissionChecker';
import { PERMISSION_NAMES } from '../../utils/AuthUtils';
import { DEFAULT_NAME_SORTER, ROLE_TYPES } from '../../utils/DataConstants';
import UploadWithSampleModal from '../flatfile/UploadWithSampleModal';
import {
	PART_MASTER_FLATFILE_HEADER,
	getFlatfileRecordForPart,
} from '../stock_locations_detail_details_page/stock_location_constants';
import moment from 'moment';
import { DATE_FORMAT } from '../../utils/DataFormatterUtils';
import { ExportToCsv } from 'export-to-csv';
import { CSV_EXPORT_DEFAULTS } from '../../utils/DataConstants';
import { getCurrency, nullSafeGet, nullSafeGetOrElse } from '../../utils/DataAccessUtils';
import { DownloadOutlined, UploadOutlined } from '@ant-design/icons';
import { vendorsRestCrudThunksForSupplier } from '../../thunks/vendors_thunks';
import PartialPartsUpload from './PartialPartsUpload';
import OWPoll from '../common/OWPoll';
import { isGLCodeAllowedBySettings } from '../../utils/InventoryUtils';
import LabelCreation from '../LabelCreation';
import { LabelEntityType } from '../LabelSystem';
import { labelsOnPartsRestCrudThunksForSupplier } from '../../thunks/labels_thunks';
import { Label, LabelOption } from '../../types/labels';
import { useFilters } from '../../hooks/useFilters';
import { getPartsFilterConfig } from '../../utils/PartsFilterUtils';

require('./PartsIndexPage.less');

const PARTS_TC = 'partsIndex';

const SAMPLE_LABELS = ['Example Label 1', 'Example Label 2'];

const getMissngPartNumbers = (existing, newList) =>
	existing
		.filter(
			(part) =>
				!newList.find(
					(record) =>
						`${part.id}` === `${record.id}` ||
						nullSafeGetOrElse('partNumber', part, -1) === record.partNumber
				)
		)
		.map((_) => nullSafeGetOrElse('partNumber', _, nullSafeGet('id', _)));

const getLabelsPayload = (
	labelsValue: string[] | undefined,
	availableLabels: Label[]
): number[] => {
	if (!Array.isArray(labelsValue) || labelsValue.length === 0) return [];
	return labelsValue
		.map((labelStr) => availableLabels.find((label) => label.label === labelStr))
		.filter((label): label is Label => label !== undefined)
		.map((label) => label.id);
};

const PartsIndexPage: FC<any> = ({
	history,
	updatePartsFilters,
	clearAndUpdateFilters,
	parts,
	fetchParts,
	fetchAllParts,
	createBulk,
	currentUser,
	fetchVendorsForUpload,
	labels,
	fetchLabels,
	uploadPartialBulk,
	getBulkUploadStatus,
	companyConfig,
}) => {
	const [downloading, setDownloading] = useState(false);
	const [exportingCsv, setExportingCsv] = useState(false);
	const [partsForUpload, setPartsForUpload] = useState([]);
	const [uploadPopupVisible, setUploadPopupVisible] = useState(false);
	const [allVendors, setAllVendors] = useState<any[]>([]);
	const [allLabels, setAllLabels] = useState<Label[]>([]);
	const [allLabelOptions, setAllLabelOptions] = useState<LabelOption[]>([]);
	const [bulkUploadInProgress, setBulkUploadInProgress] = useState(false);

	const [partialUploadPopupVisible, setPartialUploadPopupVisible] = useState(false);
	const [bulkUploadInitiated, setBulkUploadInitiated] = useState(false);

	const _hideUploadPopup = useCallback(() => setUploadPopupVisible(false), []);
	const _hidePartialUploadPopup = () => setPartialUploadPopupVisible(false);

	const userCurrency = useMemo(() => getCurrency({ currentUser }).id, [currentUser]);

	const onRow = useCallback(
		(record) => ({
			onClick: () => history.push(`/supplier/parts/detail/${record.id}`),
		}),
		[history]
	);

	const columns = useMemo(
		() => [
			{
				title: 'Name',
				dataIndex: 'name',
				sorter: true,
				render: (_, record) => <PartRowDisplay part={record} refreshParts={refreshParts} />,
			},
		],
		[]
	);

	const filters = useFilters({
		filterTargetCollectionName: PARTS_TC,
		overviewCollectionName: PARTS_TC,
		updateFilters: updatePartsFilters,
	});

	const filterConfig = useMemo(
		() =>
			getPartsFilterConfig({
				filters,
				updateFilters: updatePartsFilters,
				targetCollectionName: PARTS_TC,
			}),
		[filters, updatePartsFilters]
	);

	const getStateSliceProp = useCallback(
		(propName) => nullSafeGetOrElse(`${PARTS_TC}.${propName}`, parts, {}),
		[parts]
	);

	const refreshParts = useCallback(() => {
		fetchParts(
			{},
			PARTS_TC,
			getStateSliceProp('pagination'),
			getStateSliceProp('sorting'),
			getStateSliceProp('filters')
		);
	}, [fetchParts, getStateSliceProp]);

	const vendorOptions = useMemo(
		() =>
			allVendors.map((_) => ({
				label: `${_.name}${_.vendorId ? ` - ${_.vendorId}` : ''}`,
				value: _.id,
				alternativeNames: [_.name, ...(_.vendorId ? [_.vendorId] : [])],
			})),
		[allVendors]
	);

	const fetchAndSetAllVendors = useCallback(() => {
		return new Promise((resolve, reject) => {
			fetchVendorsForUpload()
				.then((res) => {
					setAllVendors(res);
					resolve(true);
				})
				.catch(reject);
		});
	}, [fetchVendorsForUpload]);

	const fetchLabelsSafely = useCallback(
		() =>
			new Promise<Label[]>((resolve) => {
				fetchLabels({}, PARTS_TC)
					.then((res: Label[]) => resolve(res))
					.catch(() => resolve([]));
			}),
		[fetchLabels]
	);

	const setLabelStates = useCallback((fetchedLabels: Label[]) => {
		setAllLabels(fetchedLabels);
		setAllLabelOptions(
			fetchedLabels.map((label: Label) => ({ label: label.label, value: label.label }))
		);
	}, []);

	const glCodeEnabled = useMemo(() => isGLCodeAllowedBySettings(companyConfig), [companyConfig]);

	const headerDefs = useMemo(
		() =>
			PART_MASTER_FLATFILE_HEADER([userCurrency], vendorOptions, glCodeEnabled, allLabelOptions),
		[userCurrency, vendorOptions, glCodeEnabled, allLabelOptions]
	);

	const labelsRecordHook = useCallback(
		(record) => {
			const labelsValue = record.get('labels');
			if (!labelsValue) return record;

			let rawLabels: string[] = [];
			if (typeof labelsValue === 'string' && labelsValue.trim()) {
				rawLabels = labelsValue
					.split(',')
					.map((labelChar) => labelChar.trim())
					.filter(Boolean);
			} else if (Array.isArray(labelsValue)) {
				rawLabels = labelsValue
					.flatMap((labelChar) => String(labelChar).split(','))
					.map((labelChar) => labelChar.trim())
					.filter(Boolean);
			}

			if (rawLabels.length === 0) return record;

			const validValues: string[] = [];
			const invalidLabelNames: string[] = [];
			rawLabels.forEach((labelOrId) => {
				const match = allLabelOptions.find(
					(opt) => opt.label === labelOrId || opt.value === labelOrId
				);
				if (match) {
					validValues.push(match.value);
				} else {
					invalidLabelNames.push(labelOrId);
				}
			});

			record.set('labels', validValues);
			if (invalidLabelNames.length > 0) {
				record.addError(
					'labels',
					`Unknown labels: ${invalidLabelNames.join(
						', '
					)}. Please select from existing labels only.`
				);
			}
			return record;
		},
		[allLabelOptions]
	);

	const exportAsCsv = useCallback(
		(parts, preFetchedLabels = null) => {
			const date = moment().format(DATE_FORMAT);
			const csvTitle = `Parts_Master_List_${date}`;
			const csvExporter = new ExportToCsv({
				...CSV_EXPORT_DEFAULTS,
				filename: csvTitle,
				title: csvTitle,
				headers: headerDefs.map((h) => h.label),
			});
			const allLabelsMap = nullSafeGet('records', labels) || {};
			const resolvedLabels = preFetchedLabels || Object.values(allLabelsMap);

			const data =
				parts && parts.length > 0
					? parts.map((part) => {
							const hydratedLabels =
								part.partLabels && part.partLabels.length
									? part.partLabels
									: (part.labelIds || [])
											.map((id) => resolvedLabels.find((label: Label) => label.id === id))
											.filter(Boolean);
							return getFlatfileRecordForPart(
								{ ...part, partLabels: hydratedLabels },
								userCurrency,
								headerDefs
							);
					  })
					: [getFlatfileRecordForPart({ partLabels: SAMPLE_LABELS }, userCurrency, headerDefs)];
			csvExporter.generateCsv(data);
		},
		[userCurrency, headerDefs, labels]
	);

	const onUploadClick = useCallback(() => {
		setDownloading(true);
		Promise.all([fetchAllParts(), fetchAndSetAllVendors(), fetchLabelsSafely()])
			.then(([partsRes, _vendorsRes, labelsRes]) => {
				setPartsForUpload(partsRes);
				setLabelStates(labelsRes);
				setUploadPopupVisible(true);
			})
			.catch(message.error)
			.finally(() => setDownloading(false));
	}, [fetchAllParts, fetchAndSetAllVendors, fetchLabelsSafely, setLabelStates]);

	const onPartialUploadClick = useCallback(() => {
		setDownloading(true);
		Promise.all([fetchAndSetAllVendors(), fetchLabelsSafely()])
			.then(([_vendorsRes, labelsRes]) => {
				setLabelStates(labelsRes);
				setPartialUploadPopupVisible(true);
			})
			.catch(message.error)
			.finally(() => setDownloading(false));
	}, [fetchAndSetAllVendors, fetchLabelsSafely, setLabelStates]);

	const onDownloadSample = useCallback(() => {
		exportAsCsv(partsForUpload);
	}, [exportAsCsv, partsForUpload]);

	const downloadCSV = useCallback(() => {
		setExportingCsv(true);
		const filters = parts[PARTS_TC].filters;
		Promise.all([fetchAllParts(filters), fetchLabelsSafely()])
			.then(([partsRes, labelsRes]) => {
				setPartsForUpload(partsRes);
				exportAsCsv(partsRes, labelsRes);
			})
			.finally(() => setExportingCsv(false));
	}, [exportAsCsv, fetchAllParts, fetchLabelsSafely, parts]);

	const getImagesFromExisitngPart = useCallback(
		(id) => {
			const part = partsForUpload.find((_) => `${_.id}` === `${id}`);
			return nullSafeGetOrElse('images', part, []);
		},
		[partsForUpload]
	);

	const onDataUpload = useCallback(
		(results) => {
			return new Promise((resolve, reject) => {
				const missingNumbers = getMissngPartNumbers(partsForUpload, results.validData);

				if (missingNumbers.length > 0) {
					reject(
						`Some parts are missing from existing list. Please include them! Part # ${missingNumbers.join(
							', '
						)} `
					);
				} else {
					setBulkUploadInProgress(true);
					setBulkUploadInitiated(true);
					createBulk(
						results.validData.map(({ labels: flatfileLabels, ...partData }) => ({
							...partData,
							id: partData.id ? parseInt(partData.id) : undefined,
							cost: partData.cost ? parseFloat(partData.cost) : undefined,
							images: getImagesFromExisitngPart(partData.id),
							labelIds: getLabelsPayload(flatfileLabels, allLabels),
						}))
					)
						.then((res) => {
							resolve('Updated successfully!');
							setTimeout(() => {
								_hideUploadPopup();
							}, 0);
						})
						.catch((err) => {
							reject(err || 'Unable to upload! Please contact support!');
							setBulkUploadInitiated(false);
						})
						.finally(() => setBulkUploadInProgress(false));
				}
			});
		},
		[createBulk, getImagesFromExisitngPart, _hideUploadPopup, partsForUpload, allLabels]
	);

	const onPartialDataUpload = useCallback(
		(results) => {
			return new Promise((resolve, reject) => {
				const entities = results.validData.map(({ labels: flatfileLabels, ...partData }) => ({
					...partData,
					id: partData.id ? parseInt(partData.id) : undefined,
					cost: partData.cost ? parseFloat(partData.cost) : undefined,
					labelIds: getLabelsPayload(flatfileLabels, allLabels),
				}));

				setBulkUploadInProgress(true);
				setBulkUploadInitiated(true);
				uploadPartialBulk(entities)
					.then((res) => {
						resolve('Upload initiated successfully!');
						setTimeout(() => {
							_hidePartialUploadPopup();
						}, 0);
					})
					.catch((err) => {
						reject(err || 'Unable to upload! Please contact support!');
						setBulkUploadInitiated(false);
					})
					.finally(() => setBulkUploadInProgress(false));
			});
		},
		[uploadPartialBulk, allLabels]
	);

	const uploadMenu = (
		<Menu>
			<Menu.Item onClick={onUploadClick}>All Parts</Menu.Item>
			<Menu.Item onClick={onPartialUploadClick}>Partial Upload</Menu.Item>
		</Menu>
	);

	return (
		<>
			<PaginatedReduxTableWithHeader
				targetCollectionName={PARTS_TC}
				updateFilters={updatePartsFilters}
				stateSlice={parts}
				clearAndUpdateFilters={clearAndUpdateFilters}
				filterConfig={filterConfig}
				entityCollectionName="parts"
				tableColumns={columns}
				onTableRow={onRow}
				fetchData={partsRestCrudThunksForSupplier.read}
				initialSorters={DEFAULT_NAME_SORTER}
				preAppliedFilters={{}}
				initialFilters={{ isActive: 'true' }}
				rightActions={
					<AccessPermissionChecker name={PERMISSION_NAMES.MODIFY_PARTS_AND_EQUIPMENTS}>
						<div className="flex flex-row items-center">
							<LabelCreation targetCollectionName={LabelEntityType.PART} />
							{bulkUploadInProgress ? null : (
								<AccessPermissionChecker name={PERMISSION_NAMES.ALLOW_BULK_OPERATIONS_FOR_PARTS}>
									<OWPoll
										key="parts-upload"
										id="parts-upload"
										fetchStatusPromises={[getBulkUploadStatus]}
										successMessage="Parts Upload Completed!"
										message="Parts Upload in progress!"
										onRefresh={refreshParts}
										successMessageMandatory={bulkUploadInitiated}
										renderElementWithProps={(props) => (
											<Dropdown overlay={uploadMenu} {...props}>
												<Button
													size="large"
													className="inline-block-visible-md ml-2"
													icon={<UploadOutlined />}
													loading={downloading}
												>
													<span className="inline-block-visible-xxl">Upload</span>
												</Button>
											</Dropdown>
										)}
									/>
								</AccessPermissionChecker>
							)}

							<Popover content="Export CSV" trigger="hover">
								<Button
									size="large"
									icon={<DownloadOutlined />}
									className="inline-block-visible-md ml-2"
									onClick={downloadCSV}
									loading={exportingCsv}
								>
									<span className="inline-block-visible-xxl">Export CSV</span>
								</Button>
							</Popover>
							<Button
								className="ml-2"
								type="primary"
								size="large"
								onClick={() => history.push('/supplier/parts/overview/new')}
							>
								New Part
							</Button>
						</div>
					</AccessPermissionChecker>
				}
				emptyState={
					<EmptyState
						graphic={
							<img
								style={{ marginBottom: 8 }}
								src="https://s3.amazonaws.com/mock-data-assets/categories/images/box.svg"
								alt="Add your first part"
							/>
						}
						headline={"Nothing's here!"}
						body={
							<div style={{ textAlign: 'center' }}>
								<div style={{ maxWidth: 440, marginBottom: 16 }}>
									Looks like your team has not added any parts yet.
								</div>
								<AccessPermissionChecker name={PERMISSION_NAMES.MODIFY_PARTS_AND_EQUIPMENTS}>
									<Button
										type="primary"
										onClick={() => history.push('/supplier/parts/overview/new')}
									>
										Add your first part
									</Button>
								</AccessPermissionChecker>
							</div>
						}
					/>
				}
			/>
			{uploadPopupVisible && (
				<UploadWithSampleModal
					type="Parts"
					title="Upload Parts"
					downloadText="Do you want to download the existing Parts?"
					sampleFileText="Download Parts"
					onCancel={_hideUploadPopup}
					onDownloadSample={onDownloadSample}
					fields={PART_MASTER_FLATFILE_HEADER(
						[userCurrency],
						vendorOptions,
						glCodeEnabled,
						allLabelOptions
					)}
					recordHook={labelsRecordHook}
					onDataLoad={onDataUpload}
					showWarningMessage
				/>
			)}
			{partialUploadPopupVisible && (
				<PartialPartsUpload
					onDataUpload={onPartialDataUpload}
					hideUploadPopup={_hidePartialUploadPopup}
					onUploadSuccess={refreshParts}
					vendorOptions={vendorOptions}
					labelOptions={allLabelOptions}
				/>
			)}
		</>
	);
};

const mapStateToProps = (state, ownProps) => ({
	history: ownProps.history,
	parts: state.parts,
	currentUser: state.session.currentUser,
	labels: state.labels,
	companyConfig: state.company_config.detail,
});

const mapDispatchToProps = (dispatch) => ({
	fetchParts: (params, targetCollectionName, pagination, sorting, filters, addToTargetCollection) =>
		dispatch(
			partsRestCrudThunksForSupplier.read(
				params,
				targetCollectionName,
				pagination,
				sorting,
				filters,
				addToTargetCollection
			)
		),
	updatePartsFilters: (filters, targetCollection) =>
		dispatch(PARTS_CRUD_ACTION_CREATORS.updateFilters(filters, targetCollection)),
	clearAndUpdateFilters: (filters, targetCollectionName) =>
		dispatch(PARTS_CRUD_ACTION_CREATORS.clearAndUpdateFilters(filters, targetCollectionName)),
	fetchAllParts: (params) => dispatch(fetchAllPartsForSupplier(params)),
	createBulk: (entities) => dispatch(partsRestCrudThunksForSupplier.createBulk(entities)),
	fetchVendorsForUpload: (params, targetCollectionName) =>
		dispatch(
			vendorsRestCrudThunksForSupplier.readLite(
				{ ...params, no_pagination: true },
				targetCollectionName
			)
		),
	fetchLabels: (params, targetCollectionName) =>
		dispatch(
			labelsOnPartsRestCrudThunksForSupplier.readLite(
				{ ...params, no_pagination: true },
				targetCollectionName
			)
		),
	getBulkUploadStatus: () => dispatch(getPartialPartsUploadBulkStatus(ROLE_TYPES.SUPPLIER)()),
	uploadPartialBulk: (entities) => dispatch(uploadPartialPartsBulk(ROLE_TYPES.SUPPLIER)(entities)),
});

export default withRouter<any, any>(connect(mapStateToProps, mapDispatchToProps)(PartsIndexPage));
